import React from "react";
import { Box, Stack, VStack } from "@chakra-ui/react";
import FileRequestModal from "./Modal/FileRequestModal";

import { useOverlay } from "@toss/use-overlay";

import { ReactNode, useMemo } from "react";
import { colors } from "../styles";
import { call, customEvent, getCdnImageSrc } from "../utils";
import { ColorIcon, HStack, Image } from "./common";
import { EventDomainType } from "../types";
import { useGoiCall } from "../hooks/useGoiCall";
import { useIsChuseokCallOff } from "../hooks/useChuseokOff";
import { useRouter } from "next/router";
import {
  CREMATORIUM_INQUIRY_EVENT_PATH,
  getCrematoriumPhoneByCompanyname,
} from "../constants/crematoriumInquiry";
import {
  useCrematoriumInquiryModal,
  useParlorInquiryModal,
} from "./dogsound/CrematoriumInquiry";
import {
  PARLOR_INQUIRY_EVENT_PATH,
  getParlorPhoneByRouteParam,
} from "../constants/parlorInquiry";

interface StepGuideFooterProps {
  type?: string;
  text?: ReactNode;
  eventPath?: keyof typeof EventDomainType | string;
  src?: string;
  size?: string;
  maxWidth?: string;
  bottom?: string;
  right?: string;
  iconMarginLeft?: string;
  transform?: string;
  accentColor?: string;
}

function StepGuideFooter({
  type = "DEFAULT",
  eventPath,
  size = "44px",
  maxWidth = "600px",
  right = "",
  iconMarginLeft,
  transform = "",
  src = "/step-guide/initiation",
  bottom = "40px",
  accentColor,
  text = (
    <>
      장례가 처음이신가요? 합리적인 장례,
      <br />
      장례식장과 상담하기 전 반드시 알아보세요.
    </>
  ),
}: StepGuideFooterProps) {
  const overlay = useOverlay();
  /**
   * 2026 추석. 이 바는 AllRegionLayout 을 통해 전 페이지에 깔린다.
   * 장지, 화장장 경로에서만 전화 문의 줄을 내리고 체크리스트는 그대로 둔다.
   */
  const isChuseokCallOff = useIsChuseokCallOff();
  const typeToStyle = useMemo(() => {
    if (type === "DEFAULT") {
      return {
        background: colors.white,
        color: colors.gray800,
      };
    } else if (type === "DARK") {
      return {
        background: colors.gray800,
        color: colors.white,
      };
    } else {
      return {
        background: colors.white,
        color: colors.gray800,
      };
    }
  }, [type]);

  const { goiCall } = useGoiCall();

  /**
   * 화장장 시설 상세에서는 바로 걸지 않고 문의 유형 모달을 띄운다.
   * 이 고정바는 6개 포털 전 페이지에 깔려서 라우트로 판정한다. 지역 목록은
   * 어느 화장장인지 정해지지 않아 대상이 아니다(CREMATORIUM_INQUIRY_EVENT_PATH 참고).
   */
  const router = useRouter();
  const inquiryEventPath = CREMATORIUM_INQUIRY_EVENT_PATH[router.pathname];
  const parlorInquiryEventPath = PARLOR_INQUIRY_EVENT_PATH[router.pathname];
  /** 화장장 상세가 아니면 모달을 열지 않으므로 이때 넘기는 값은 쓰이지 않는다. */
  const { openInquiryModal } = useCrematoriumInquiryModal({
    eventDomain: (eventPath as keyof typeof EventDomainType) || "common",
    eventPath: inquiryEventPath || "footer",
  });
  /** 장례식장 상세가 아니면 모달을 열지 않으므로 이때 넘기는 path 는 쓰이지 않는다. */
  const { openInquiryModal: openParlorInquiryModal } = useParlorInquiryModal({
    eventDomain: (eventPath as keyof typeof EventDomainType) || "common",
    eventPath: parlorInquiryEventPath || "footer",
  });

  return (
    <Stack alignItems="center" width="100%" flexDirection="column">
      <Box
        style={{
          background: "#00000044",
          width: "100%",
          margin: 0,
          position: "fixed",
          bottom: 0,
          right: right,
          zIndex: 5,
          display: "flex",
          justifyContent: "center",
          transform: transform,
          boxShadow: "2px 10px 12px 10px #00000066",
        }}
      >
        <VStack width="100%" maxWidth={maxWidth} gap={0}>
          <HStack
            width="100%"
            background="#ffffffdd"
            borderBottom="1px solid #00000033"
            padding="0"
          >
            {/* <HStack
              as="button"
              background="none"
              width="50%"
              padding="12px 12px"
              className="body_14_m gray700"
              gap={6}
              alignItems="center"
              justifyContent="center"
              border="none"
              style={{
                borderRight: "1px solid #00000033",
              }}
              onClick={() => {
                customEvent({
                  domain: "common",
                  path: "footer",
                  trigger_and_target: "footer2_discount_chat",
                });
                window.open("http://pf.kakao.com/_xgFDaK/chat");
              }}
            >
              <Box textAlign="center">
                교육, 경찰, 소방
                <br />
                공무원 할인 문의 👨‍💼
              </Box>
              <ColorIcon
                name="arrow_right_small"
                size={24}
                color={typeToStyle.color}
                style={{ marginLeft: iconMarginLeft || "0px" }}
              />
            </HStack> */}

            {!isChuseokCallOff && (
            <HStack
              as="button"
              border="none"
              background="none"
              width="100%"
              padding="12px 6px"
              className="body_14_m gray800"
              gap={12}
              alignItems="center"
              justifyContent="center"
              onClick={() => {
                customEvent({
                  domain: "common",
                  path: "footer",
                  trigger_and_target: "footer3_call",
                });

                if (inquiryEventPath) {
                  openInquiryModal(
                    getCrematoriumPhoneByCompanyname(router.query.companyname)
                  );
                  return;
                }

                if (parlorInquiryEventPath) {
                  openParlorInquiryModal(
                    getParlorPhoneByRouteParam(
                      router.query.id ?? router.query.companyname
                    )
                  );
                  return;
                }

                goiCall();
              }}
            >
              <Box textAlign="center">
                장례 절차, 전화 문의
                <br />
                임종 접수(24시간)
              </Box>
              <ColorIcon
                name="icon_call_mono"
                size={24}
                color={typeToStyle.color}
                style={{ marginLeft: iconMarginLeft || "0px" }}
              />
            </HStack>
            )}
          </HStack>

          <Stack
            as="button"
            flexDirection="row"
            onClick={() => {
              customEvent({
                domain: "common",
                path: "footer",
                trigger_and_target: "footer1_checklist_modal",
              });
              overlay.open(({ isOpen, close: onClose }) => {
                return (
                  <FileRequestModal
                    path={eventPath as string}
                    isOpen={isOpen}
                    onClose={onClose}
                    accentColor={accentColor}
                  />
                );
              });
            }}
            width="100%"
            border="none"
            background="#ffffff"
            padding="12px 20px 12px 40px"
            alignItems="center"
            justifyContent="center"
            gap="12px"
          >
            <Image
              src={getCdnImageSrc(
                `platform/home/match-request-version/circle-guidebook.png`
              )}
              alt="step-guide"
              width={size}
              height={size}
            />
            <Box
              className="subtitle_14_b"
              textAlign="left"
              color={typeToStyle.color}
            >
              장례비용 예상 견적표,
              <br />
              임종준비 체크리스트 받아보기
            </Box>
            <ColorIcon
              name="icon-arrow-right-circle-mono"
              size={36}
              color={typeToStyle.color}
              style={{ marginLeft: iconMarginLeft || "0px" }}
            />
          </Stack>
        </VStack>
      </Box>
    </Stack>
  );
}

export default StepGuideFooter;
